import React, { useMemo, useState, createElement } from 'react';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import PageHeader from '../components/PageHeader';
import MetricCard from '../components/MetricCard';
import SegmentDetailDrawer from '../components/drawers/SegmentDetailDrawer';
import { AIOutlineButton } from '../components/ui/AIOutlineButton';
import { AISidebar } from '../components/AISidebar';
import { AIRecommendationPanel } from '../components/ai/AIRecommendationPanel';
import { useAdminData } from '../contexts/AdminDataContext';
import { Users, Activity, DollarSign, TrendingUp, Download, TrendingDown } from 'lucide-react';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
type PeriodFilter = '7days' | '30days' | '90days' | 'thisMonth';
// Mock AI recommendations for platform analytics
const getPlatformAnalyticsRecommendations = () => [{
  id: '1',
  title: 'Crescita utenti in accelerazione',
  description: "La crescita degli utenti è aumentata del 12.5% nell'ultimo periodo. Mantieni questo trend investendo in canali di acquisizione performanti.",
  type: 'trend' as const,
  priority: 'high' as const,
  impact: 'Consolidamento della crescita organica',
  cta: 'Vedi strategie'
}, {
  id: '2',
  title: 'Ottimizza il retention rate',
  description: 'Il retention rate è al 68%, ma alcuni segmenti mostrano performance inferiori. Focus su engagement personalizzato per migliorare.',
  type: 'opportunity' as const,
  priority: 'high' as const,
  impact: 'Potenziale aumento del 15% nel retention',
  cta: 'Analizza segmenti'
}, {
  id: '3',
  title: 'Revenue trend positivo',
  description: 'Il revenue è cresciuto del 15.7% con un trend costante. Considera strategie di upselling per massimizzare il valore per utente.',
  type: 'recommendation' as const,
  priority: 'medium' as const,
  impact: 'Aumento del 20% nel revenue per utente',
  cta: 'Esplora opportunità'
}];
const PlatformAnalytics = () => {
  const {
    platformAnalytics,
    showToast
  } = useAdminData();
  const [periodFilter, setPeriodFilter] = useState<PeriodFilter>('30days');
  const [selectedSegment, setSelectedSegment] = useState<any>(null);
  const [showSegmentDrawer, setShowSegmentDrawer] = useState(false);
  const [isAISidebarOpen, setIsAISidebarOpen] = useState(false);
  // Filter data based on period
  const filteredData = useMemo(() => {
    const today = new Date();
    let daysToShow = 30;
    switch (periodFilter) {
      case '7days':
        daysToShow = 7;
        break;
      case '30days':
        daysToShow = 30;
        break;
      case '90days':
        daysToShow = 90;
        break;
      case 'thisMonth':
        daysToShow = today.getDate();
        break;
    }
    const cutoffDate = new Date(today);
    cutoffDate.setDate(cutoffDate.getDate() - daysToShow);
    return {
      usersSeries: platformAnalytics.usersSeries.filter(item => new Date(item.date) >= cutoffDate),
      revenueSeries: platformAnalytics.revenueSeries.filter(item => new Date(item.date) >= cutoffDate)
    };
  }, [periodFilter, platformAnalytics]);
  const handleExportReport = () => {
    // Mock CSV export
    const csvContent = [['Date', 'Users', 'Revenue'], ...filteredData.usersSeries.map((item, idx) => [item.date, item.value, filteredData.revenueSeries[idx]?.value || 0])].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], {
      type: 'text/csv'
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `platform-analytics-${periodFilter}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    showToast('Report esportato con successo!');
  };
  const handleSegmentClick = (segment: any) => {
    setSelectedSegment(segment);
    setShowSegmentDrawer(true);
  };
  const handleCloseSegmentDrawer = () => {
    setShowSegmentDrawer(false);
    setTimeout(() => setSelectedSegment(null), 300);
  };
  const recommendations = getPlatformAnalyticsRecommendations().map(rec => ({
    ...rec,
    onCtaClick: () => console.log('CTA clicked:', rec.id)
  }));
  return <div className="flex h-screen bg-gray-50" data-id="element-3494">
      <div className="w-20 flex-shrink-0" data-id="element-3495">
        <Sidebar data-id="element-3496" />
      </div>

      <div className="flex-1 flex flex-col overflow-hidden" data-id="element-3497">
        <Topbar data-id="element-3498" />
        <main className="flex-1 overflow-y-auto p-4 md:p-6" data-id="element-3499">
          {/* Header with filters and AI button */}
          <div className="flex items-start justify-between mb-6" data-id="element-3500">
            <div data-id="element-3501">
              <h1 className="text-xl font-semibold text-gray-900" data-id="element-3502">
                Analitiche Piattaforma
              </h1>
              <p className="text-sm font-normal text-gray-500 mt-1" data-id="element-3503">
                Panoramica completa delle performance della piattaforma
              </p>
            </div>
            <div className="flex items-center gap-3" data-id="element-3504">
              <select value={periodFilter} onChange={e => setPeriodFilter(e.target.value as PeriodFilter)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-normal focus:outline-none focus:ring-2 focus:ring-green-600" data-id="element-3505">
                <option value="7days" data-id="element-3506">Ultimi 7 giorni</option>
                <option value="30days" data-id="element-3507">Ultimi 30 giorni</option>
                <option value="90days" data-id="element-3508">Ultimi 90 giorni</option>
                <option value="thisMonth" data-id="element-3509">Questo mese</option>
              </select>
              <AIOutlineButton onClick={() => setIsAISidebarOpen(true)} data-id="element-3510">
                Suggerimenti AI
              </AIOutlineButton>
            </div>
          </div>

          {/* KPI Cards - Changed gap-4 to gap-6 for consistency with widgets */}
          <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4" data-id="element-3511">
            <MetricCard title="Utenti Totali" value={platformAnalytics.kpis.totalUsers.toLocaleString()} change="12.5%" isPositive={true} icon={Users} data-id="element-3512" />
            <MetricCard title="Engagement Rate" value={`${platformAnalytics.kpis.engagementRate}%`} change="8.3%" isPositive={true} icon={Activity} data-id="element-3513" />
            <MetricCard title="Revenue" value={`€${platformAnalytics.kpis.revenue.toLocaleString()}`} change="15.7%" isPositive={true} icon={DollarSign} data-id="element-3514" />
            <MetricCard title="Retention" value={`${platformAnalytics.kpis.retention}%`} change="3.2%" isPositive={true} icon={TrendingUp} data-id="element-3515" />
          </div>

          {/* Charts Row */}
          <div className="mb-8 grid grid-cols-1 gap-6 lg:grid-cols-2" data-id="element-3516">
            {/* User Growth Chart */}
            <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm" data-id="element-3517">
              <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-3518">
                Crescita Utenti
              </h3>
              <div className="h-80" data-id="element-3519">
                <ResponsiveContainer width="100%" height="100%" data-id="element-3520">
                  <LineChart data={filteredData.usersSeries} data-id="element-3521">
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" data-id="element-3522" />
                    <XAxis dataKey="date" stroke="#6B7280" fontSize={12} tickFormatter={value => {
                    const date = new Date(value);
                    return `${date.getDate()}/${date.getMonth() + 1}`;
                  }} data-id="element-3523" />
                    <YAxis stroke="#6B7280" fontSize={12} data-id="element-3524" />
                    <Tooltip contentStyle={{
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E5E7EB',
                    borderRadius: '0.5rem'
                  }} labelFormatter={value => {
                    const date = new Date(value);
                    return date.toLocaleDateString('it-IT');
                  }} formatter={(value: any) => [value.toLocaleString(), 'Utenti']} data-id="element-3525" />
                    <Line type="monotone" dataKey="value" stroke="#108d3e" strokeWidth={2} dot={false} name="Utenti" data-id="element-3526" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Revenue Trend Chart */}
            <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm" data-id="element-3527">
              <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-3528">
                Trend Revenue
              </h3>
              <div className="h-80" data-id="element-3529">
                <ResponsiveContainer width="100%" height="100%" data-id="element-3530">
                  <AreaChart data={filteredData.revenueSeries} data-id="element-3531">
                    <defs data-id="element-3532">
                      <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1" data-id="element-3533">
                        <stop offset="5%" stopColor="#108d3e" stopOpacity={0.3} data-id="element-3534" />
                        <stop offset="95%" stopColor="#108d3e" stopOpacity={0} data-id="element-3535" />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" data-id="element-3536" />
                    <XAxis dataKey="date" stroke="#6B7280" fontSize={12} tickFormatter={value => {
                    const date = new Date(value);
                    return `${date.getDate()}/${date.getMonth() + 1}`;
                  }} data-id="element-3537" />
                    <YAxis stroke="#6B7280" fontSize={12} data-id="element-3538" />
                    <Tooltip contentStyle={{
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E5E7EB',
                    borderRadius: '0.5rem'
                  }} labelFormatter={value => {
                    const date = new Date(value);
                    return date.toLocaleDateString('it-IT');
                  }} formatter={(value: any) => [`€${value.toLocaleString()}`, 'Revenue']} data-id="element-3539" />
                    <Area type="monotone" dataKey="value" stroke="#108d3e" strokeWidth={2} fillOpacity={1} fill="url(#colorRevenue)" name="Revenue" data-id="element-3540" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Segment Breakdown Table */}
          <div className="rounded-lg border border-gray-200 bg-white overflow-hidden" data-id="element-3541">
            <div className="px-6 py-4 border-b border-gray-200" data-id="element-3542">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-3543">
                Breakdown per Segmento
              </h3>
              <p className="text-sm font-normal text-gray-500 mt-1" data-id="element-3544">
                Analisi dettagliata per canale di acquisizione
              </p>
            </div>
            <div className="overflow-x-auto" data-id="element-3545">
              <table className="w-full" data-id="element-3546">
                <thead data-id="element-3547">
                  <tr className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3548">
                    <th className="px-6 py-3" data-id="element-3549">Segmento</th>
                    <th className="px-6 py-3" data-id="element-3550">Utenti</th>
                    <th className="px-6 py-3" data-id="element-3551">Engagement</th>
                    <th className="px-6 py-3" data-id="element-3552">Revenue</th>
                    <th className="px-6 py-3" data-id="element-3553">Trend</th>
                    <th className="px-6 py-3 text-right" data-id="element-3554">Azioni</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200" data-id="element-3555">
                  {platformAnalytics.segments.map(segment => <tr key={segment.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleSegmentClick(segment)} data-id="element-3556">
                      <td className="px-6 py-4" data-id="element-3557">
                        <div className="font-medium text-gray-900" data-id="element-3558">
                          {segment.name}
                        </div>
                      </td>
                      <td className="px-6 py-4 font-medium text-gray-900" data-id="element-3559">
                        {segment.users.toLocaleString()}
                      </td>
                      <td className="px-6 py-4" data-id="element-3560">
                        <div className="flex items-center" data-id="element-3561">
                          <div className="flex-1 bg-gray-200 rounded-full h-2 mr-2 max-w-[100px]" data-id="element-3562">
                            <div className="bg-green-600 h-2 rounded-full" style={{
                          width: `${segment.engagement}%`
                        }} data-id="element-3563" />
                          </div>
                          <span className="text-sm font-medium text-gray-900" data-id="element-3564">
                            {segment.engagement}%
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 font-medium text-gray-900" data-id="element-3565">
                        €{segment.revenue.toLocaleString()}
                      </td>
                      <td className="px-6 py-4" data-id="element-3566">
                        <div className="flex items-center" data-id="element-3567">
                          {segment.trend > 0 ? <TrendingUp className="h-4 w-4 text-green-600 mr-1" data-id="element-3568" /> : <TrendingDown className="h-4 w-4 text-red-600 mr-1" data-id="element-3569" />}
                          <span className={`text-sm font-medium ${segment.trend > 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-3570">
                            {segment.trend > 0 ? '+' : ''}
                            {segment.trend}%
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right" data-id="element-3571">
                        <button onClick={e => {
                      e.stopPropagation();
                      handleSegmentClick(segment);
                    }} className="text-green-600 hover:text-green-700 font-medium text-sm" data-id="element-3572">
                          Dettagli
                        </button>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>
        </main>
      </div>

      {/* Segment Detail Drawer */}
      <SegmentDetailDrawer isOpen={showSegmentDrawer} onClose={handleCloseSegmentDrawer} segment={selectedSegment} data-id="element-3573" />

      {/* AI Sidebar */}
      <AISidebar isOpen={isAISidebarOpen} onClose={() => setIsAISidebarOpen(false)} title="Suggerimenti AI: Analitiche Piattaforma" data-id="element-3574">
        <AIRecommendationPanel recommendations={recommendations} data-id="element-3575" />
      </AISidebar>
    </div>;
};
export default PlatformAnalytics;